<template>
  <div>
    <ui-modal v-model="isVisible">
      <div
        v-if="pendingWallet === null"
        class="p-8 text-center"
      >
        <h3 class="mb-4 text-lg font-medium text-gray-800 dark:text-gray-200">
          Sign In
        </h3>
        <div class="mb-4 text-gray-600 dark:text-gray-400">
          Choose a way to sign in to authenticate.
        </div>
        <div class="mx-auto space-y-3 sm:max-w-xs">
          <ui-button
            block
            :is-loading="isConnectingWallet"
            size="lg"
            class="flex items-center"
            @click="connectWallet"
          >
            <i class="mr-2 fas fa-wallet" />
            Connect With Wallet
          </ui-button>

          <div
            v-if="error"
            class="
              p-3
              mt-2
              text-sm
              text-center
              text-red-600
              rounded-lg
              bg-red-600 bg-opacity-20
              ring-red-600 ring-offset-0
            "
          >
            {{ error }}
          </div>
        </div>
      </div>
      <div
        v-if="pendingWallet !== null"
        class="p-8 text-center"
      >
        <h3 class="mb-4 text-lg font-medium text-gray-800 dark:text-gray-200">
          Link New Wallet
        </h3>
        <div class="mb-4 text-sm text-gray-600 dark:text-gray-400">
          You just connected a new wallet. You can link this wallet to your
          account and use it to sign in.
        </div>
        <div class="mb-4 text-sm text-gray-600 dark:text-gray-400">
          As long as one of your linked wallets holds a Lifetime Pass you'll be
          able to use all Compass features.
        </div>
        <div class="mx-auto space-y-3 sm:max-w-xs">
          <ui-button
            block
            :is-loading="isLinkingWallet"
            :disabled="isConnectingWallet"
            size="lg"
            @click="linkWallet(false)"
          >
            Link wallet to account
          </ui-button>
          <ui-button
            block
            :is-loading="isConnectingWallet"
            :disabled="isLinkingWallet"
            size="lg"
            variant="white"
            @click="connectWallet"
          >
            Re-authenticate with wallet
          </ui-button>

          <div
            v-if="error"
            class="
              p-3
              mt-2
              text-sm
              text-center
              text-red-600
              rounded-lg
              bg-red-600 bg-opacity-20
              ring-red-600 ring-offset-0
            "
          >
            {{ error }}
          </div>
        </div>
      </div>
    </ui-modal>
    <ui-modal v-model="isWaitingForSignature">
      <div class="p-8 text-center">
        <h3 class="mb-4 text-lg font-medium text-gray-800 dark:text-gray-200">
          Signing in
        </h3>
        <!-- <div class="text-gray-600 dark:text-gray-400">Please accept the signature request in your {{providerName}} wallet to sign in.</div> -->

        <div class="flex justify-center items-center mt-8 space-x-5">
          <!-- <ui-button @click="resetWallet">Change Wallet</ui-button> -->
          <loading-indicator class="text-indigo-500" />
        </div>
      </div>
    </ui-modal>
  </div>
</template>

<script>
/* eslint-disable */
import { defineComponent } from "vue";
import { useUiDialog } from "@/components/ui-dialog/useUiDialog";

import UiModal from "@/components/ui/UiModal.vue";
import UiButton from "@/components/ui/UiButton.vue";
import UiLink from "@/components/ui/UiLink.vue";
import LoadingIndicator from "./LoadingIndicator.vue";
import eventBus from "@/utils/event-bus";

export default defineComponent({
  name: "ConnectWalletModal",

  components: { UiLink, UiButton, UiModal, LoadingIndicator },

  setup() {
    const { info } = useUiDialog();

    return {
      info,
    }
  },

  async mounted() {
    eventBus.$on("auth:signIn", () => {
      this.isVisible = true;
      this.pendingWallet = null;
      this.error = null;
    });

    eventBus.$on('auth:signInWithDiscord', async (code) => {
      this.finishSignInWithDiscord(code);
    });

    this.setupListeners();
  },
  data() {
    return {
      // Flags
      isVisible: false,
      isWaitingForSignature: false,
      isConnectingWallet: false,
      isLinkingWallet: false,
      isSigningInWithDiscord: false,
      // Data
      pendingWallet: null,
      error: null,
    };
  },
  computed: {},
  methods: {
    setupListeners(provider = null) {
      if (!provider && !window.ethereum) {
        return;
      }

      if (!provider && window.ethereum) {
        provider = window.ethereum;
      }

      provider.on("chainChanged", (chainId) => {
        /* handle the chainId */
        console.log("Chain changed");
      });
      provider.on("accountsChanged", ([account]) => {
        if (this.isConnectingWallet) {
          return;
        }
        console.log("accounts changed", account);

        // we need to check linked wallets
        if (this.$store.getters["auth/isWalletLinked"](account)) {
          this.isVisible = false;
          this.pendingWallet = null;
          return;
        }

        this.isVisible = true;
        this.pendingWallet = account;
      });
      provider.on("disconnect", (msg) => {
        console.log("disconnected");
      });
    },
    async linkWallet(force = false) {
      this.error = null;
      this.isLinkingWallet = true;
      try {
        const provider = await this.$store.dispatch('auth/getProvider');

        await this.$store.dispatch("auth/linkWallet", {provider, force});
        this.isVisible = false;
        this.pendingWallet = null;
        this.info('Wallet linked successfully!');
      } catch (e) {
        if (e.message !== 'WALLET_ALREADY_LINKED') {
          this.error = e.message;

          return;
        }

        if (!confirm('WARNING: The wallet that you connected is linked to an extisting Compass account. Please confirm that you want to link it anyway.')) {
          return;
        }

        this.$nextTick(() => {
          this.linkWallet(true);
        })

        return;
        
      } finally {
        this.isLinkingWallet = false;
      }
    },
    async connectWallet() {
      this.error = null;
      this.isConnectingWallet = true;
      // we fetch a provider from the web3 modal
      // and pass it to the store method
      try {
        const provider = await this.$store.dispatch('auth/getProvider');

        await this.$store.dispatch("auth/signInWithWallet", provider);
        this.isVisible = false;
        this.info('Welcome!');
        eventBus.$emit('auth:didSignIn');
      } catch (e) {
        console.error("Failed to connect with wallet: " + e.message);
      } finally {
        this.isConnectingWallet = false;
      }
    },
    signInWithDiscord() {
      this.isSigningInWithDiscord = true;
      const url = process.env.VUE_APP_DISCORD_AUTH_REDIRECT_URL;
      window.location.href = url;
    },
    async finishSignInWithDiscord(code) {
      this.error = null;
      this.isVisible = true;
      this.isSigningInWithDiscord = true;
      try {
        await this.$store.dispatch('auth/signInWithDiscord', code);
        this.isVisible = false;
        eventBus.$emit('auth:didSignIn');
      } catch (e) {
        this.error = e.message;
      } finally {
        this.isSigningInWithDiscord = false;
      }
    }
  },
});
</script>

<style scoped>
.discord-button {
  background: #5865F2;
  color: #fff;
}
.discord-button:hover {
  background: #717cf4;
  color: #fff;
}
</style>
